import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '~/api';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import { CustomField, CustomFieldCreate, CustomFieldUpdate } from '~/types';
import { isErrorMessage } from '~/utils/errorResponse';

const useQueryKey = () => {
  const storeId = useStoreId();
  return ['customFields', storeId] as const;
};

export const useCustomFields = () => {
  const queryKey = useQueryKey();

  return useQuery({
    queryKey,
    queryFn: ({ queryKey: [, storeId] }) =>
      api.store(storeId).customFields.get(),
  });
};

export const useCustomFieldsUpsert = () => {
  const queryClient = useQueryClient();
  const storeId = useStoreId();
  const queryKey = useQueryKey();
  const toast = useToast();

  return useMutation({
    mutationFn: (customField: CustomFieldUpdate | CustomFieldCreate) =>
      'id' in customField
        ? api
            .store(storeId)
            .customFields.update(String(customField.id), customField)
        : api.store(storeId).customFields.create(customField),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onMutate: (upsert) => {
      const customFields =
        queryClient.getQueryData<CustomField[]>(queryKey) ?? [];

      const updates = [...customFields];

      if ('id' in upsert) {
        const index = updates.findIndex(({ id }) => id === upsert.id);
        if (index === -1) return null; // TODO error
        const current = updates[index];
        if (!current) return null; // TODO error

        updates.splice(index, 1, {
          // if abstracted would need to handled deep merges
          ...current,
          ...upsert,
        });
      } else {
        updates.push({ ...upsert, id: Number.MAX_SAFE_INTEGER });
      }

      queryClient.setQueryData(queryKey, updates);

      return { previousGroups: customFields };
    },
    onError: (error, _, context) => {
      queryClient.setQueryData(queryKey, context?.previousGroups);

      const message = isErrorMessage(error)
        ? error.message
        : 'Something went wrong. Please try again.';

      toast.show(message);
      return error;
    },
  });
};

export const useCustomFieldsDelete = () => {
  const queryClient = useQueryClient();
  const storeId = useStoreId();
  const queryKey = useQueryKey();
  const toast = useToast();

  return useMutation({
    mutationFn: (customField: CustomField) =>
      api.store(storeId).customFields.delete(String(customField.id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onMutate: (customField: CustomField) => {
      const previousCustomFields =
        queryClient.getQueryData<CustomField[]>(queryKey) ?? [];

      queryClient.setQueryData(
        queryKey,
        previousCustomFields.filter((g) => g.id !== customField.id),
      );

      return { previousGroups: previousCustomFields };
    },
    onError: (error, _, context) => {
      queryClient.setQueryData(queryKey, context?.previousGroups);

      toast.show(
        isErrorMessage(error)
          ? error.message
          : 'Something went wrong. Please try again.',
      );
    },
  });
};
